'use client';

/**
 * usePaygValidation - PAYG payment validation hook
 *
 * Flow: Create validation → Poll if pending → Handle result
 */

import { useState, useCallback, useRef } from 'react';
import { usePurchaseLog } from '../config/PurchaseFlowConfigProvider';
import { createPaymentValidation, getPaymentValidation, PaymentValidationResponse } from '../api';
import { poll } from '../utils/polling';
import type { ValidationStatus, UsePaygValidationOptions, UsePaygValidationReturn } from '../types';

export const usePaygValidation = ({
  paymentSourceId,
  advisorId,
  conversationType,
  context,
  methodName,
  onSuccess,
  onError
}: UsePaygValidationOptions): UsePaygValidationReturn => {
  const log = usePurchaseLog();
  const [status, setStatus] = useState<ValidationStatus>('idle');
  const [message, setMessage] = useState('');
  const hasStartedRef = useRef(false);

  const handleError = useCallback((errorMsg: string) => {
    setStatus('error');
    setMessage(errorMsg);
    onError(errorMsg);
  }, [onError]);

  const reset = useCallback(() => {
    hasStartedRef.current = false;
    setStatus('idle');
    setMessage('');
  }, []);

  const pollForResult = async (validationId: number): Promise<PaymentValidationResponse> => {
    setStatus('polling');
    setMessage('Checking validation status...');

    return poll({
      pollFunction: () => getPaymentValidation(validationId),
      checkComplete: (r) => r.state === 'validated' || r.state === 'failed',
      log
    });
  };

  const startValidation = useCallback(async () => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;

    try {
      setStatus('creating');
      setMessage(`Validating ${ methodName }...`);

      log('info', 'PAYG Validation', `Starting ${ methodName } validation`, {
        paymentSourceId, advisorId, conversationType
      });

      const response = await createPaymentValidation(paymentSourceId, {
        advisorId, context, orderType: conversationType
      });

      log('info', 'PAYG Validation', 'Validation created', {
        validationId: response.id, state: response.state
      });

      // Poll only if pending, otherwise use initial response
      const result: PaymentValidationResponse = response.state === 'pending'
        ? await pollForResult(response.id)
        : response;

      // Handle result
      if (result.state === 'validated') {
        log('info', 'PAYG Validation', `${ methodName } validation successful`, { validationId: response.id });
        onSuccess({ success: true, validationId: response.id, okMessage: result.ok_message });
      } else {
        log('error', 'PAYG Validation', `${ methodName } validation failed`, {
          validationId: response.id, error: result.error_message
        });
        handleError(result.error_message || 'Validation failed');
      }
    } catch (error) {
      log('error', 'PAYG Validation', `${ methodName } validation error`, { error });
      handleError(error instanceof Error ? error.message : 'Validation failed');
    }
  }, [paymentSourceId, advisorId, conversationType, context, methodName, onSuccess, handleError]);

  return {
    status, message, startValidation, reset
  };
};
